import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentListIcon,
  LockClosedIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import LandingLayout from '../components/layout/LandingLayout';

const steps = [
  {
    num: '1',
    title: 'Your provider invites you',
    desc: "You'll receive a secure link from your therapist, psychologist, or counselor to set up your client portal account.",
  },
  {
    num: '2',
    title: 'Book and attend sessions',
    desc: 'Schedule appointments online, join telehealth sessions from any device, and set reminders — no phone tag required.',
  },
  {
    num: '3',
    title: 'Stay connected between sessions',
    desc: 'Send secure messages, complete intake forms, and view invoices — all in one private, HIPAA-compliant space.',
  },
];

const features = [
  {
    icon: CalendarDaysIcon,
    title: 'Easy online scheduling',
    desc: "View your provider's real-time availability and book sessions 24/7 — from any device.",
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'Secure messaging',
    desc: 'Message your provider privately and securely. No unsecured email — your conversations stay protected.',
  },
  {
    icon: CreditCardIcon,
    title: 'Transparent billing',
    desc: 'View and pay invoices online, download superbills for insurance reimbursement, and track your payment history.',
  },
  {
    icon: ClipboardDocumentListIcon,
    title: 'Intake forms & documents',
    desc: 'Complete intake paperwork digitally before your first session. No clipboards, no fax machines.',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Works on any device',
    desc: 'Access your client portal from your phone, tablet, or computer — no app download required.',
  },
  {
    icon: LockClosedIcon,
    title: 'Private & confidential',
    desc: 'Your health information is encrypted and protected. We take your privacy as seriously as your provider does.',
  },
];

const faqs = [
  {
    q: 'Do I need to download an app?',
    a: 'No downloads required. Your client portal works in any modern browser on your phone, tablet, or computer.',
  },
  {
    q: 'Is my health information private?',
    a: 'Yes. Sage is HIPAA-compliant and uses AES-256 encryption. Your information is only accessible to you and your care team.',
  },
  {
    q: 'How do I join a telehealth session?',
    a: 'Just click the link in your appointment reminder or from your portal dashboard. No software installation needed.',
  },
  {
    q: 'Can I pay my invoices online?',
    a: 'Yes. You can pay invoices securely by credit card directly through your client portal.',
  },
  {
    q: 'What if I need to reschedule?',
    a: "You can reschedule or cancel appointments through your portal according to your provider's cancellation policy.",
  },
];

export default function ForClients() {
  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-900 via-primary-900 to-primary-800 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(6,182,212,0.2),_transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-medium text-cyan-200 mb-6 border border-white/10">
              <HeartIcon className="w-4 h-4" />
              For Clients
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-5">
              Your care,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-primary-300">
                always within reach
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-cyan-100 leading-relaxed mb-8 max-w-2xl">
              The Sage client portal gives you a private, secure space to book appointments, message your provider,
              and manage your care — on your schedule.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
              >
                Access your portal
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-xl border border-white/20 transition-colors"
              >
                How it works
              </a>
            </div>
            <p className="mt-4 text-sm text-cyan-300">Your portal is set up by your provider — no sign-up required</p>
          </div>
        </div>

        {/* Decorative portal mockup */}
        <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 w-80">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span className="ml-2 text-xs text-white/50">Client Portal</span>
            </div>
            <div className="space-y-3">
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-xs text-cyan-300 mb-1">Next appointment</p>
                <p className="text-sm font-semibold text-white">Thursday, Apr 24 · 3:00 PM</p>
                <p className="text-xs text-white/60">Video session with Dr. Kim</p>
                <button className="mt-2 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg transition-colors">
                  Join session
                </button>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-xs text-cyan-300 mb-2">Unread message</p>
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-cyan-400/40 flex-shrink-0 flex items-center justify-center text-xs text-white font-semibold">K</div>
                  <div>
                    <p className="text-xs font-medium text-white">Dr. Kim</p>
                    <p className="text-xs text-white/60">Just checking in — how did the breathing exercise go...</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-xs text-cyan-300 mb-1">Invoice ready</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">$150.00</p>
                    <p className="text-xs text-white/60">Session · Apr 17</p>
                  </div>
                  <button className="text-xs bg-success-500/30 text-success-300 px-3 py-1 rounded-lg">
                    Pay now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">
              Getting started is simple
            </h2>
            <p className="mt-4 text-neutral-500 max-w-xl mx-auto">
              Your provider sets up your portal. All you need to do is log in.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-10 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-200 to-cyan-200" />
            {steps.map(({ num, title, desc }) => (
              <div key={num} className="text-center relative">
                <div className="w-16 h-16 bg-primary-50 border-2 border-primary-200 rounded-full flex items-center justify-center mx-auto mb-5 relative z-10">
                  <span className="text-xl font-extrabold text-primary-600">{num}</span>
                </div>
                <h3 className="text-base font-semibold text-neutral-900 mb-2">{title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">Your portal includes</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">
              Everything you need to manage your care
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-neutral-100 p-6 shadow-card">
                <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">{title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy section */}
      <section className="py-16 bg-primary-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-shrink-0 w-16 h-16 bg-primary-800 rounded-2xl flex items-center justify-center">
              <ShieldCheckIcon className="w-9 h-9 text-primary-300" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3">Your privacy is non-negotiable</h2>
              <p className="text-primary-300 leading-relaxed max-w-2xl">
                Sage is fully HIPAA-compliant. Your health information is encrypted, never sold, and only accessible
                to you and your care team. We sign a Business Associate Agreement with every practice that uses Sage —
                so you can trust your data is protected at every step.
              </p>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'HIPAA Compliant', icon: ShieldCheckIcon },
              { label: 'AES-256 Encrypted', icon: LockClosedIcon },
              { label: 'Never sold', icon: HeartIcon },
              { label: 'You own your data', icon: CheckCircleIcon },
            ].map(({ label, icon: Icon }) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <Icon className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <span className="text-sm text-primary-200 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map(({ q, a }) => (
              <div key={q} className="bg-neutral-50 border border-neutral-100 rounded-2xl p-5">
                <p className="font-semibold text-neutral-900 mb-2">{q}</p>
                <p className="text-sm text-neutral-500 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-700 to-primary-800 text-white text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeartIcon className="w-10 h-10 text-cyan-300 mx-auto mb-4" />
          <h2 className="text-3xl font-extrabold mb-3">Ready to access your portal?</h2>
          <p className="text-cyan-100 mb-8">
            Your provider will send you an invitation. Already have an account? Log in now.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
            >
              Log in to my portal <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <Link
              to="/for-providers"
              className="text-sm font-medium text-cyan-200 hover:text-white transition-colors"
            >
              Are you a provider? →
            </Link>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
